import { Logger } from '../support/Logger';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';

export class IndexedDBFactory<T extends { id: IDBValidKey }> {
    static inject = () => [Logger, MixPanelEventEmitter];
    constructor(private logger: Logger, private tracker: MixPanelEventEmitter) {}

    // either creates or returns an existing DB
    get = (dbName: string, storeName: string, version: number = 1) => {
        return new IndexedDBManager<T>(dbName, storeName, version, this.logger, this.tracker);
    };
};

export class IndexedDBManager<T extends { id: IDBValidKey }> {
    private dbPromise: Promise<IDBDatabase>;
    private hasReinitialized = false;

    constructor(private dbName: string, private storeName: string, private version: number = 1, private logger: Logger, private tracker: MixPanelEventEmitter) {
        this.dbName = dbName;
        this.storeName = storeName;
        this.version = version;
        this.logger = logger;
        this.dbPromise = this.initializeDB();
    };

    private initializeDB = async (): Promise<IDBDatabase> => {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onerror = (event) => {
                reject(`IndexedDB error: ${(event.target as IDBRequest).error}`);
            };

            request.onsuccess = (event) => {
                resolve((event.target as IDBRequest).result);
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBRequest).result as IDBDatabase;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, { keyPath: 'id' });
                }
            };
        });
    };

    private reInitializeDB = async (postInitialization?: () => Promise<any>, operation?: 'add' | 'update' | 'get' | 'delete' | 'getAll'): Promise<void> => {
        if (this.hasReinitialized) {
            this.logger.error('IndexedDB reinitialization already attempted, preventing infinite loop');
            return;
        }

        this.hasReinitialized = true;
        const db = await this.dbPromise;
        try {
            db.close();
        } catch (error) {
            // maybe it can't close, as it's not connected? do nothing
        } finally {
            this.dbPromise = this.initializeDB();
            if (postInitialization) {
                try {
                    await postInitialization();
                    // Track successful post-initialization operation
                    if (operation) {
                        this.tracker.track('IndexedDB Reinitialized Success', () => ({
                            operation
                        }));
                    }
                } catch (error) {
                    this.logger.error(`Post-initialization ${operation} operation failed:`, error);
                }
            }
        }
    };

    async getCapacityEstimate() {
        const estimate = await navigator.storage.estimate();
        console.log(estimate);
        return estimate;
    };

    private getTransaction = async () => {
        let tx: IDBTransaction;
        const db = await this.dbPromise;

        try {
            tx = db.transaction(this.storeName, 'readwrite');
        } catch(err: any) {
            if (err.name === 'InvalidStateError') {
                await this.initializeDB();
                tx = db.transaction(this.storeName, 'readwrite');
            } else {
                throw err;
            }
        }

        return tx;
    };

    private executeRequest = async <R>(params: {
        operation: 'add' | 'update' | 'get' | 'delete' | 'getAll';
        getRequest: () => Promise<IDBRequest>;
        retryOperation: () => Promise<R>;
    }): Promise<R> => {
        const { operation, getRequest, retryOperation } = params;

        return new Promise(async (resolve, reject) => {
            try {
                const request = await getRequest();

                request.onsuccess = (event) => resolve((event.target as IDBRequest).result as R);
                request.onerror = async (event) => {
                    const message = `Error ${operation} data: ${(event.target as IDBRequest).error}`;
                    this.logger.error(message);

                    if (!this.hasReinitialized) {
                        try {
                            await this.reInitializeDB(async () => {
                                const result = await retryOperation();
                                resolve(result);
                                return result;
                            }, operation);
                        } catch (reinitError) {
                            reject(reinitError);
                        }
                    } else {
                        reject(message);
                    }
                };
            } catch (error) {
                reject(error);
            }
        });
    };

    async add(data: T): Promise<IDBValidKey> {
        return this.executeRequest<IDBValidKey>({
            operation: 'add',
            getRequest: async () => {
                const tx = await this.getTransaction();
                const store = tx.objectStore(this.storeName);
                return store.add(data);
            },
            retryOperation: () => this.add(data)
        });
    };

    async get(id: IDBValidKey): Promise<T | undefined> {
        return this.executeRequest<T | undefined>({
            operation: 'get',
            getRequest: async () => {
                const tx = await this.getTransaction();
                const store = tx.objectStore(this.storeName);
                return store.get(id);
            },
            retryOperation: () => this.get(id)
        });
    };

    async update(data: T): Promise<void> {
        return new Promise(async (resolve, reject) => {
            try {
                const tx = await this.getTransaction();
                const store = tx.objectStore(this.storeName);
                const request = store.get(data.id);

                request.onsuccess = (event) => {
                    const existingData = (event.target as IDBRequest).result as T;
                    if (existingData) {
                        // Merge existing data with updated data
                        const mergedData = { ...existingData, ...data };
                        const putRequest = store.put(mergedData);

                        putRequest.onsuccess = () => resolve();
                        putRequest.onerror = (errEvent) => reject(`Error updating data: ${(errEvent.target as IDBRequest).error}`);
                    } else {
                        reject(new Error(`Record with key ${data.id} not found`));
                    }
                };

                request.onerror = async (_event) => {
                    const message = `Error getting data: ${(_event.target as IDBRequest).error}`;
                    this.logger.error(message);

                    if (!this.hasReinitialized) {
                        try {
                            await this.reInitializeDB(async () => {
                                await this.update(data);
                                resolve();
                            }, 'update');
                        } catch (reinitError) {
                            reject(reinitError);
                        }
                    } else {
                        reject(message);
                    }
                };
            } catch (error) {
                reject(error);
            }
        });
    };

    async delete(id: IDBValidKey): Promise<void> {
        const result = await this.executeRequest<void>({
            operation: 'delete',
            getRequest: async () => {
                const tx = await this.getTransaction();
                const store = tx.objectStore(this.storeName);
                return store.delete(id);
            },
            retryOperation: () => this.delete(id)
        });
        console.log(`deleted recording with id ${id}`);
        return result;
    };

    async getAll(): Promise<T[]> {
        return this.executeRequest<T[]>({
            operation: 'getAll',
            getRequest: async () => {
                const tx = await this.getTransaction();
                const store = tx.objectStore(this.storeName);
                return store.getAll();
            },
            retryOperation: () => this.getAll()
        });
    };
};
